import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Injector,
  type Signal,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import type { DocumentResponse, FolderResponse } from '@tourfold/libraries/tf-api-client-public-v2';
import { FoldersService } from '../services/folders.service';
import { DocumentsService } from '../services/documents.service';
import { StarsService } from '../services/stars.service';
import { translateProblem } from '../services/translate-problem';
import {
  DocumentManagementPreferencesService,
  type FolderSortKey,
} from '../services/document-management-preferences.service';
import { FolderColumnComponent } from './folder-column.component';
import { SmartListColumnComponent } from './smart-list-column.component';
import { NewFolderDialogComponent } from '../new-folder-dialog/new-folder-dialog.component';
import {
  DetailPaneComponent,
  type DetailSelection,
  type RenameNotice,
} from '../detail-pane/detail-pane.component';

export type StarTarget =
  | { readonly kind: 'folder'; readonly id: string }
  | { readonly kind: 'document'; readonly id: string };

export type SmartListKind = 'recent' | 'starred' | 'deleted';

/** Translation key for a smart-list breadcrumb label. */
function smartListKindLabelKey(kind: SmartListKind): string {
  switch (kind) {
    case 'recent': return 'DOCUMENT_MANAGEMENT.RECENT';
    case 'starred': return 'DOCUMENT_MANAGEMENT.STARRED';
    case 'deleted': return 'DOCUMENT_MANAGEMENT.DELETED';
  }
}

interface ColumnState {
  /** `null` = tenant root (i.e. listChildren without a parentId). */
  readonly parentId: string | null;
  /** Header label — parent folder's display name, or `null` for the tenant-root fallback. */
  readonly parentLabel: string | null;
  /**
   * Kind of this column's own folder (`parentId`). `null` for the tenant root, smart-list
   * columns, or before a scoped root's kind has resolved. Passed down to the column so it can
   * gate its upload / "New folder" affordances — `managed_branch` folders (e.g. `/objects`,
   * `/objects/<type>`) are structural and host no content, so the backend rejects uploads (422)
   * and folder creation (403) there regardless of the user's grants.
   */
  readonly parentKind?: FolderResponse['kind'] | null;
  /** id of the child the user has drilled into; drives highlighting + the next column. */
  selectedChildId: string | null;
  /** id of a document the user clicked in this column (mutually exclusive with `selectedChildId`). */
  selectedDocumentId: string | null;
  /**
   * When set, this column is a flat list view (Recent or Starred) instead of a normal
   * `parentId`-based folder column. Renders `<tf-smart-list-column>` and ignores parentId.
   */
  readonly smart?: SmartListKind;
  /** When set on column 0, marks which smart entry is currently selected (for highlighting). */
  selectedSmart?: SmartListKind | null;
  /** Bumped by the browser to force the column to reload (e.g. after a sibling create). */
  reloadKey: number;
}

/**
 * Miller-column folder browser with a right-hand detail pane. Each column shows the children of
 * one folder; selecting a folder drills in by appending a new column to the right and surfaces it
 * in the detail pane. Selecting a document highlights it and shows its metadata in the detail pane
 * — documents are leaves and do not drill.
 *
 * Inputs:
 *   - `rootFolderId` — id to use as the leftmost column's parent. `null` (default) = tenant root.
 *   - `initialFolderId` — optional deep-link target. On init, the browser walks parents from
 *     this id up to `rootFolderId` (or the tenant root) so the trail is restored.
 *
 * Output:
 *   - `currentFolderIdChange` — emitted whenever the user navigates. Hosts can sync to URL.
 */
interface SortOption {
  readonly value: FolderSortKey;
  readonly label: string;
}

const SORT_OPTION_KEYS: readonly { value: FolderSortKey; labelKey: string }[] = [
  { value: 'name-asc', labelKey: 'DOCUMENT_MANAGEMENT.SORT.NAME_ASC' },
  { value: 'name-desc', labelKey: 'DOCUMENT_MANAGEMENT.SORT.NAME_DESC' },
  { value: 'uploaded-desc', labelKey: 'DOCUMENT_MANAGEMENT.SORT.UPLOADED_DESC' },
  { value: 'uploaded-asc', labelKey: 'DOCUMENT_MANAGEMENT.SORT.UPLOADED_ASC' },
  { value: 'size-desc', labelKey: 'DOCUMENT_MANAGEMENT.SORT.SIZE_DESC' },
  { value: 'size-asc', labelKey: 'DOCUMENT_MANAGEMENT.SORT.SIZE_ASC' },
];

@Component({
  selector: 'tf-folder-browser',
  imports: [
    TranslateModule,
    FormsModule,
    SelectModule,
    CheckboxModule,
    FolderColumnComponent,
    SmartListColumnComponent,
    NewFolderDialogComponent,
    DetailPaneComponent,
  ],
  templateUrl: './folder-browser.component.html',
  styleUrl: './folder-browser.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FolderBrowserComponent {
  private readonly foldersService = inject(FoldersService);
  private readonly documentsService = inject(DocumentsService);
  private readonly starsService = inject(StarsService);
  private readonly translate = inject(TranslateService);
  private readonly preferencesService = inject(DocumentManagementPreferencesService);
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  protected readonly folderSort = computed(() => this.preferencesService.folderSort());
  protected readonly showTags = computed(() => this.preferencesService.showTags());

  /**
   * Pre-translated so PrimeNG's `optionLabel="label"` lands a real string in the combobox's
   * accessible name; binding the i18n key directly leaks the raw key (e.g. `…SORT.SIZE_DESC`)
   * to assistive tech. Re-built whenever the active language changes.
   */
  protected readonly sortOptions = computed<SortOption[]>(() => {
    this.langChangeTick();
    return SORT_OPTION_KEYS.map((option) => ({
      value: option.value,
      label: this.translate.instant(option.labelKey),
    }));
  });

  private readonly langChangeTick = signal(0);

  protected onFolderSortChange(value: FolderSortKey): void {
    this.preferencesService.setFolderSort(value);
  }

  protected onShowTagsChange(value: boolean): void {
    this.preferencesService.setShowTags(value);
  }

  readonly rootFolderId = input<string | null>(null);
  readonly initialFolderId = input<string | null>(null);
  /**
   * Optional document id to pre-select on init. When set, the browser opens its containing
   * folder (if reachable from `rootFolderId`/the tenant root) and shows the document in the
   * detail pane. Used by the page to restore selection across reloads via the `?doc=` query
   * param.
   */
  readonly initialDocumentId = input<string | null>(null);
  /** Label for column 0's header — caller provides "Documents" for embedded use, defaults to a generic root label. */
  readonly rootLabel = input<string | null>(null);
  /**
   * Optional smart-list view to open on init ("recent" or "starred"). Used by the page to
   * restore `?view=` deep links across reloads. Ignored when `initialFolderId`/`initialDocumentId`
   * is set — those take precedence.
   */
  readonly initialView = input<SmartListKind | null>(null);

  readonly currentFolderIdChange = output<string | null>();
  /** Emits whenever the user clicks a document, or `null` when navigating away from one. */
  readonly currentDocumentIdChange = output<string | null>();
  /** Emits the active smart-list view, or `null` when the user is back in folder navigation. */
  readonly currentViewChange = output<SmartListKind | null>();

  protected readonly columns = signal<ColumnState[]>([
    { parentId: null, parentLabel: null, selectedChildId: null, selectedDocumentId: null, selectedSmart: null, reloadKey: 0 },
  ]);

  protected readonly newFolderDialogVisible = signal(false);
  protected readonly newFolderParentId = signal<string | null>(null);
  protected readonly isCreatingFolder = signal(false);
  protected readonly newFolderError = signal<string | null>(null);

  /** Active selection driving the detail pane: a folder or a document, or null at the root. */
  protected readonly selection = signal<DetailSelection | null>(null);

  /** Counts for the detail pane's folder subtitle: documents + subfolders directly under it. */
  protected readonly folderChildCounts = signal<{ folders: number; documents: number } | null>(null);

  /**
   * Per-user starred set, populated once via `GET /starred`. Backend list responses don't carry
   * `starred` per row to avoid the per-row lookup, so columns and detail panes intersect this set
   * by id when rendering the toggle.
   */
  protected readonly starredIds = signal<ReadonlySet<string>>(new Set());

  /**
   * Translated error message surfaced when a star/unstar call fails — for example, attempting to
   * star a soft-deleted row returns 404 with `data.reason = "soft-deleted"` per the slice-C rule.
   * Passed down to the detail pane so it can render the message in its lifecycle-error banner;
   * cleared on selection change so leftover errors don't bleed across rows.
   */
  protected readonly starError = signal<string | null>(null);

  protected readonly selectedDocumentIdInLastColumn = computed<string | null>(
    () => this.columns().at(-1)?.selectedDocumentId ?? null,
  );

  /**
   * Tracks the document id we've already restored on this trail so the effect doesn't repeat
   * the work when other inputs (e.g. `rootFolderId`) trigger a re-fire. `null` means "not yet
   * restored"; an empty string means "input is null and there's nothing to restore".
   */
  private restoredDocumentId: string | null | undefined = undefined;

  /**
   * Track previous scope inputs so the effect can distinguish "scope changed → rebuild trail"
   * from "URL `?doc=` changed via our own emit → only update selection". Without this guard the
   * effect re-runs the column reset every time the user clicks a document, since the page
   * mirrors the new doc id back into the `initialDocumentId` signal — which made the columns
   * (and detail pane) flicker on every selection.
   */
  private lastEffectRoot: string | null | undefined = undefined;
  private lastEffectInitialFolder: string | null | undefined = undefined;
  private lastEffectInitialView: SmartListKind | null | undefined = undefined;

  /** Tracks the last selection id so {@link starError} only clears when the user actually moves. */
  private lastStarErrorSelectionId: string | null | undefined = undefined;

  constructor() {
    this.translate.onLangChange.subscribe(() => this.langChangeTick.update((v) => v + 1));
    this.loadStarredIds();

    // Drop any in-flight star-error message when the user navigates to a different row. Without
    // this, an "soft-deleted item can't be starred" message would follow the user into the next
    // row's detail pane and read as if the new row was the failing one.
    effect(() => {
      const sel = this.selection();
      const id = sel?.id ?? null;
      if (this.lastStarErrorSelectionId !== undefined && this.lastStarErrorSelectionId !== id) {
        this.starError.set(null);
      }
      this.lastStarErrorSelectionId = id;
    });
    // Auto-scroll the columns container after navigation so the user lands on something useful.
    // Reacts to both `selection()` and `columns()` because some navigations only change one of
    // them — e.g. clicking Recent/Starred/Deleted appends a smart column but leaves selection
    // null, so a `selection()`-only effect wouldn't fire (TF-1774).
    //
    // Rules, in order:
    //   - document selection → scroll to the right edge so the detail pane (which renders the
    //     document preview) is the visible piece.
    //   - everything else (folder selection, smart-list selection, breadcrumb collapse, initial
    //     mount with null selection) → align the rightmost folder/smart column's left edge with
    //     the container's left edge. When only one column exists this naturally resolves to
    //     scrollLeft = 0, so a breadcrumb-back-to-root resets the scroll.
    //
    // The no-overflow early-return keeps desktop layouts (which fit the whole trail + pane)
    // untouched in every case.
    effect(() => {
      const sel = this.selection();
      // Read so the effect re-fires on column changes (e.g. smart-list append with null selection).
      this.columns();
      afterNextRender(
        () => {
          const columns = this.hostRef.nativeElement.querySelector<HTMLElement>(
            '.folder-browser__columns',
          );
          if (!columns) return;
          if (columns.scrollWidth <= columns.clientWidth) return;
          if (sel?.kind === 'document') {
            columns.scrollTo({ left: columns.scrollWidth, behavior: 'smooth' });
            return;
          }
          const columnElements = columns.querySelectorAll<HTMLElement>(
            'tf-folder-column, tf-smart-list-column',
          );
          const lastColumn = columnElements[columnElements.length - 1];
          if (!lastColumn) return;
          const colRect = columns.getBoundingClientRect();
          const lastRect = lastColumn.getBoundingClientRect();
          const targetScrollLeft = columns.scrollLeft + (lastRect.left - colRect.left);
          columns.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
        },
        { injector: this.injector },
      );
    });

    effect(() => {
      const root = this.rootFolderId();
      const initial = this.initialFolderId();
      const initialDoc = this.initialDocumentId();
      const initialView = this.initialView();

      // Scope (root + folder + smart-view) determines the column trail. When only `doc`
      // changed — typically because the user clicked a row and we mirrored that into `?doc=`
      // via the parent — the columns shouldn't be rebuilt. Without this guard every doc
      // selection flickered the whole browser.
      const scopeUnchanged = this.lastEffectRoot === root
        && this.lastEffectInitialFolder === initial
        && this.lastEffectInitialView === initialView;
      this.lastEffectRoot = root;
      this.lastEffectInitialFolder = initial;
      this.lastEffectInitialView = initialView;

      if (scopeUnchanged) {
        this.applyDocChangeWithinScope(initialDoc);
        return;
      }
      this.rebuildTrail(root, initial, initialDoc, initialView);
    });
  }

  /**
   * Adjust selection in response to a `?doc=` URL change while the column trail stays put.
   * No column rebuild — that's what the previous "always reset" effect was doing and it's the
   * source of the per-click flicker.
   */
  private applyDocChangeWithinScope(initialDoc: string | null): void {
    // Self-emitted echo: `onDocumentSelected` / `onRecentDocumentClicked` set `selection`
    // synchronously and then emit `currentDocumentIdChange`, which the page mirrors into `?doc=`
    // and feeds straight back here. The trail and detail-pane selection are already correct, so
    // re-running `restoreDocumentSelection` would re-walk the whole trail via `openTrail` and
    // re-set `selection` to the same document — re-triggering the detail pane's loading branch and
    // flashing the content out and back in on every click (TF-1716). Only fall through to a real
    // restore for a genuine external deep-link (reload / paste / back-forward) where the selection
    // isn't already on this document.
    const current = this.selection();
    if (initialDoc && current?.kind === 'document' && current.id === initialDoc) {
      this.restoredDocumentId = initialDoc;
      return;
    }
    if (initialDoc && this.restoredDocumentId !== initialDoc) {
      this.restoreDocumentSelection(initialDoc);
      return;
    }
    if (!initialDoc && this.selection()?.kind === 'document') {
      // `?doc=` was cleared externally (back-button, paste). Drop the document selection but
      // keep the folder trail intact.
      const last = this.columns().at(-1);
      this.columns.update((cols) =>
        cols.map((c, i) => (i === cols.length - 1 ? { ...c, selectedDocumentId: null } : c)),
      );
      const lastParentId = last?.parentId ?? null;
      this.selection.set(lastParentId ? { kind: 'folder', id: lastParentId } : null);
      this.restoredDocumentId = '';
    }
  }

  /** Full reset of the column trail when the scope (root / initial folder / view) changes. */
  private rebuildTrail(
    root: string | null,
    initial: string | null,
    initialDoc: string | null,
    initialView: SmartListKind | null,
  ): void {
    if (initial) {
      this.openTrail(initial, root, initialDoc);
      return;
    }
    const baseCol: ColumnState = {
      parentId: root, parentLabel: null, selectedChildId: null,
      selectedDocumentId: null, selectedSmart: null, reloadKey: 0,
    };
    // `?view=` only applies at the unscoped tenant root — no smart shortcuts inside a scoped
    // browser (e.g. embedded on an object's documents tab).
    //
    // When `?view=deleted` is combined with `?doc=` (the user reloaded while looking at an
    // soft-deleted doc inside the Trash smart entry), set the smart trail and let the doc-only
    // branch below handle selection without rebuilding the folder trail. Without this, an
    // soft-deleted doc was restored via `restoreDocumentSelection` walking up to its containing
    // folder — that drilled out of the Trash and lost the user's context.
    if (initialView && root === null && !initialDoc) {
      this.columns.set([
        { ...baseCol, selectedSmart: initialView },
        { parentId: null, parentLabel: null, selectedChildId: null, selectedDocumentId: null, smart: initialView, reloadKey: 0 },
      ]);
      this.selection.set(null);
      this.folderChildCounts.set(null);
      return;
    }
    if (initialView && root === null && initialDoc) {
      this.columns.set([
        { ...baseCol, selectedSmart: initialView },
        { parentId: null, parentLabel: null, selectedChildId: null, selectedDocumentId: initialDoc, smart: initialView, reloadKey: 0 },
      ]);
      this.selection.set({ kind: 'document', id: initialDoc });
      this.folderChildCounts.set(null);
      this.restoredDocumentId = initialDoc;
      return;
    }
    this.columns.set([baseCol]);
    // Leave selection at null on init even when scoped to a `rootFolderId`. The root of a scoped
    // browser is always a managed/managed_branch folder (e.g. custom-object docs tab) for which
    // the detail pane would render the empty state anyway — pre-selecting would just trigger a
    // wasted folder fetch and a brief spinner flash before the empty state appears.
    this.selection.set(null);
    this.folderChildCounts.set(null);
    // Only resolve the scope folder's display name when the caller didn't supply an explicit
    // `rootLabel` — otherwise we'd overwrite the caller's label (e.g. "Documents") with the
    // underlying canonical name (e.g. an instance UUID).
    if (root && !this.rootLabel()) this.resolveRootLabel(root);
    if (initialDoc) {
      this.restoreDocumentSelection(initialDoc);
    }
  }

  /**
   * Fetches the calling user's starred set on init. Failures leave the set empty — the user just
   * doesn't see star highlights until the next reload, which is acceptable degradation.
   */
  private async loadStarredIds(): Promise<void> {
    try {
      const response = await this.starsService.listAll();
      const ids = new Set<string>();
      for (const entry of response.items ?? []) {
        const id = entry.kind === 'folder' ? entry.folder?.id : entry.document?.id;
        if (id) ids.add(id);
      }
      this.starredIds.set(ids);
    } catch {
      // Leave starredIds empty.
    }
  }

  /**
   * Optimistically flips the local starred set, calls the matching star/unstar endpoint, and
   * rolls back on error. The server response is idempotent and authoritative on the new state,
   * but we don't refresh from it — the optimistic update already reflects the desired result.
   */
  protected async onToggleStar(target: StarTarget): Promise<void> {
    const isStarredNow = this.starredIds().has(target.id);
    this.starredIds.update((ids) => {
      const next = new Set(ids);
      if (isStarredNow) next.delete(target.id);
      else next.add(target.id);
      return next;
    });
    this.starError.set(null);
    try {
      if (target.kind === 'folder' && isStarredNow) {
        await this.starsService.unstarFolder(target.id);
      } else if (target.kind === 'folder') {
        await this.starsService.starFolder(target.id);
      } else if (isStarredNow) {
        await this.starsService.unstarDocument(target.id);
      } else {
        await this.starsService.starDocument(target.id);
      }
    } catch (err) {
      this.starredIds.update((ids) => {
        const next = new Set(ids);
        if (isStarredNow) next.add(target.id);
        else next.delete(target.id);
        return next;
      });
      // Surface the failure so the detail pane can render an inline message — silent rollback
      // misled users into thinking the click was lost. The most common failure today is the
      // soft-delete-row rejection (slice C: stars are bound to live state); the problem-translation
      // pipeline maps `document-management/{folder|document}-not-found` with `data.reason =
      // "deleted"` to the `DOCUMENT_DELETED` user-facing message.
      const message = await translateProblem(
        err,
        this.translate,
        isStarredNow ? 'DOCUMENT_MANAGEMENT.UNSTAR_FAILED' : 'DOCUMENT_MANAGEMENT.STAR_FAILED',
      );
      this.starError.set(message);
    }
  }

  /**
   * Restores a deep-linked document selection on init. Fetches the document detail to learn its
   * `folderId`, opens that folder's trail, then sets the document as the active selection. The
   * detail pane fetches its own data from `selection`, so we just need to point at the right id.
   */
  private async restoreDocumentSelection(documentId: string): Promise<void> {
    if (this.restoredDocumentId === documentId) return;
    this.restoredDocumentId = documentId;
    try {
      const doc = await this.documentsService.getById(documentId);
      const folderId = doc.folderId;
      if (doc.softDeletedAt != null) {
        // A stale `?doc=` deep link to a now-soft-deleted document (e.g. reloading after a delete,
        // or an old shared link) must not reopen it in the folder browser — that's the same lingering
        // preview the delete path avoids. Show the parent folder instead and drop the stale `?doc=`.
        // Restore lives in the Deleted view.
        if (folderId) {
          await this.openTrail(folderId, this.rootFolderId(), null);
        } else {
          this.selection.set(null);
        }
        this.currentDocumentIdChange.emit(null);
        return;
      }
      if (folderId) {
        await this.openTrail(folderId, this.rootFolderId(), documentId);
      } else {
        this.selection.set({ kind: 'document', id: documentId });
      }
    } catch {
      // Document is gone; leave selection at the folder context.
    }
  }

  /**
   * When the browser is scoped (`rootFolderId` set), fetch that folder once to label column 0 and
   * record its kind so the column can gate its upload / "New folder" affordances. Scoped roots are
   * usually `managed` (a custom-object instance folder, a valid content host), but resolving the
   * kind keeps a hypothetical `managed_branch` scope correctly read-only.
   */
  private async resolveRootLabel(rootId: string): Promise<void> {
    try {
      const folder = await this.foldersService.getById(rootId);
      const label = folder.displayName ?? folder.name ?? null;
      this.columns.update((cols) =>
        cols.map((c, i) => (i === 0 ? { ...c, parentLabel: label, parentKind: folder.kind ?? null } : c)),
      );
    } catch {
      // Leave the fallback root label in place.
    }
  }

  /**
   * Click on the "Recent" or "Starred" virtual entry in column 0. Truncates the column stack so
   * column 0 stays visible (with the smart entry highlighted) and replaces column 1 with a single
   * `<tf-smart-list-column>`. Clicking another smart entry swaps the smart column's `kind`.
   */
  protected onSmartSelected(columnIdx: number, kind: SmartListKind): void {
    this.columns.update((cols) => {
      const truncated = cols.slice(0, columnIdx + 1).map((c, i) =>
        i === columnIdx
          ? { ...c, selectedSmart: kind, selectedChildId: null, selectedDocumentId: null }
          : c,
      );
      return [
        ...truncated,
        { parentId: null, parentLabel: null, selectedChildId: null, selectedDocumentId: null, smart: kind, reloadKey: 0 },
      ];
    });
    this.selection.set(null);
    this.folderChildCounts.set(null);
    // Pre-record the trail scope we just set so the effect — which fires once the URL settles
    // back through `initial*` inputs — sees scope-unchanged and skips rebuilding the trail
    // we built locally. Without this guard the trail flickered: local set → URL settle →
    // effect rebuild from the now-current inputs.
    this.lastEffectInitialFolder = null;
    this.lastEffectInitialView = kind;
    // Emit only `currentViewChange` so the page can handle path + ?doc= + ?view= in a single
    // `router.navigate`. Three separate emits race when fired back-to-back: the second's
    // `merge` reads the unsettled URL of the first and the `?doc=` removal can be lost.
    this.currentViewChange.emit(kind);
  }

  protected onFolderSelected(columnIdx: number, folder: FolderResponse): void {
    const id = folder.id;
    if (!id) return;
    // Folder clicks coming from a smart column behave per kind:
    //   - Recent/Starred: drill into the folder's real path (replace the smart trail) so the
    //     user can browse its children. Those rows are live folders.
    //   - Trash: don't drill — soft-deleted folders have no live children to show in a column.
    //     Just highlight the row in the smart column and surface its detail pane (where the
    //     user can Restore or Delete). The "Trash" smart entry stays selected so the
    //     breadcrumb keeps the user's "I came from Trash" context.
    const sourceColumn = this.columns()[columnIdx];
    if (sourceColumn?.smart === 'deleted') {
      this.columns.update((cols) =>
        cols.slice(0, columnIdx + 1).map((c, i) =>
          i === columnIdx ? { ...c, selectedChildId: id, selectedDocumentId: null } : c,
        ),
      );
      this.selection.set({ kind: 'folder', id });
      this.refreshFolderChildCounts(id);
      // No view emit — `?view=deleted` should stay on the URL.
      return;
    }
    if (sourceColumn?.smart) {
      // Pre-record scope so the effect (URL settle) skips its rebuild, since `openTrail` is
      // already constructing the trail asynchronously here.
      this.lastEffectInitialFolder = id;
      this.lastEffectInitialView = null;
      this.openTrail(id, this.rootFolderId(), null);
      // Single emit: the page handler clears `?view=` and `?doc=` atomically alongside the path
      // change, so we don't need separate `currentDocumentIdChange` / `currentViewChange` emits
      // (those would race the path nav and could lose the folder id).
      this.currentFolderIdChange.emit(id);
      return;
    }
    const label = folder.displayName ?? folder.name ?? null;
    this.columns.update((cols) => {
      const truncated = cols.slice(0, columnIdx + 1).map((c, i) =>
        i === columnIdx ? { ...c, selectedChildId: id, selectedDocumentId: null, selectedSmart: null } : c,
      );
      return [
        ...truncated,
        { parentId: id, parentLabel: label, parentKind: folder.kind ?? null, selectedChildId: null, selectedDocumentId: null, reloadKey: 0 },
      ];
    });
    this.selection.set({ kind: 'folder', id });
    this.refreshFolderChildCounts(id);
    // Pre-record scope so the URL-settle effect treats this as a no-op.
    this.lastEffectInitialFolder = id;
    this.lastEffectInitialView = null;
    // Single emit; page handler resets `?doc=` and `?view=` atomically with the path change.
    this.currentFolderIdChange.emit(id);
  }

  /**
   * The folder detail pane's "Recently added" section emits this when the user clicks a row.
   * The clicked document may live anywhere in the selected folder's subtree; rather than walk
   * to it (which would change the column trail and surprise the user), we just point the detail
   * pane at it and emit a `?doc=<id>` URL update.
   */
  protected onRecentDocumentClicked(doc: DocumentResponse): void {
    if (!doc.id) return;
    this.selection.set({ kind: 'document', id: doc.id });
    this.folderChildCounts.set(null);
    this.currentDocumentIdChange.emit(doc.id);
  }

  /**
   * Empty-space click inside a folder column. Behaves like clicking that column's breadcrumb:
   * any deeper columns collapse, the column's own child/document/smart selections are cleared,
   * and the detail pane surfaces the column's parent folder. So clicking left columns in a deep
   * trail walks the user back to that level — not just the rightmost column.
   */
  protected onColumnBackgroundClicked(columnIdx: number): void {
    this.collapseToColumn(columnIdx);
  }

  protected onDocumentSelected(columnIdx: number, doc: DocumentResponse): void {
    const id = doc.id;
    if (!id) return;
    this.columns.update((cols) =>
      cols.slice(0, columnIdx + 1).map((c, i) =>
        i === columnIdx ? { ...c, selectedDocumentId: id, selectedChildId: null } : c,
      ),
    );
    this.selection.set({ kind: 'document', id });
    this.folderChildCounts.set(null);
    this.currentDocumentIdChange.emit(id);
  }

  /**
   * Breadcrumb chain. Crumb 0 represents the leftmost column's parent (root or scoped folder);
   * each subsequent crumb represents a column the user has drilled into. Clicking crumb `i`
   * truncates the column stack so that column `i` becomes the rightmost — same behavior as
   * clicking back to that column directly.
   */
  protected readonly breadcrumbs = computed(() => {
    const cols = this.columns();
    const root = this.rootLabel() ?? this.translate.instant('DOCUMENT_MANAGEMENT.ROOT_LABEL');
    return cols.map((col) => {
      if (col.smart) {
        return { label: this.translate.instant(smartListKindLabelKey(col.smart)) };
      }
      return { label: col.parentLabel ?? root };
    });
  });

  protected onCrumbClick(crumbIndex: number): void {
    this.collapseToColumn(crumbIndex);
  }

  /**
   * Shared "make column N the rightmost" path used by both breadcrumb clicks and empty-space
   * clicks inside a column body. Truncates deeper columns, clears the target column's child /
   * document / smart selections, sets the detail pane to the column's parent folder, and emits
   * URL change events so the page can update `?folder=` / `?doc=` / `?view=`. Bails out early
   * when the operation would be a no-op (rightmost column with nothing selected) so we don't
   * generate spurious URL replaces.
   */
  private collapseToColumn(columnIdx: number): void {
    const cols = this.columns();
    const target = cols[columnIdx];
    if (!target) return;
    const isRightmost = columnIdx === cols.length - 1;
    const hasNothingToClear =
      target.selectedChildId === null
      && target.selectedDocumentId === null
      && (target.selectedSmart ?? null) === null;
    if (isRightmost && hasNothingToClear) return;

    this.columns.update((current) =>
      current.slice(0, columnIdx + 1).map((c, i) =>
        i === columnIdx
          ? { ...c, selectedChildId: null, selectedDocumentId: null, selectedSmart: null }
          : c,
      ),
    );
    const parentId = target.parentId;
    this.selection.set(parentId ? { kind: 'folder', id: parentId } : null);
    this.refreshFolderChildCounts(parentId);
    // Pre-record scope so the URL-settle effect treats this as a no-op. The page handler for
    // `currentFolderIdChange` clears `?doc=` and `?view=` atomically — separate emits here
    // raced and triggered three router navigations whose `merge` snapshots stomped each other.
    this.lastEffectInitialFolder = parentId;
    this.lastEffectInitialView = null;
    this.currentFolderIdChange.emit(parentId);
  }

  /** Column 0's header label falls back to `rootLabel()` (or the generic "Root" key). */
  protected columnHeaderLabel(column: ColumnState, index: number): string {
    if (column.parentLabel) return column.parentLabel;
    if (index === 0) {
      return this.rootLabel() ?? this.translate.instant('DOCUMENT_MANAGEMENT.ROOT_LABEL');
    }
    return '';
  }

  protected onNewFolderRequested(parentId: string | null): void {
    this.newFolderParentId.set(parentId);
    this.newFolderError.set(null);
    this.newFolderDialogVisible.set(true);
  }

  protected onNewFolderCancel(): void {
    this.newFolderDialogVisible.set(false);
    this.newFolderError.set(null);
  }

  protected onNewFolderVisibilityChange(visible: boolean): void {
    this.newFolderDialogVisible.set(visible);
    if (!visible) this.newFolderError.set(null);
  }

  protected async onNewFolderSave(name: string): Promise<void> {
    const parentId = this.newFolderParentId();
    this.isCreatingFolder.set(true);
    this.newFolderError.set(null);
    try {
      await this.foldersService.create({
        name,
        parentId: parentId ?? undefined,
      });
      this.newFolderDialogVisible.set(false);
      // Bump the reloadKey on the column whose parentId received the new child so its effect
      // re-fires `load()`. Single update path — avoids the double-load flicker the imperative
      // ref approach caused.
      this.columns.update((cols) =>
        cols.map((c) =>
          c.parentId === parentId ? { ...c, reloadKey: c.reloadKey + 1 } : c,
        ),
      );
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.CREATE_FOLDER_ERROR',
      );
      this.newFolderError.set(message);
    } finally {
      this.isCreatingFolder.set(false);
    }
  }

  /**
   * Refresh affected columns + trail labels after an inline rename in the detail pane.
   *
   * - For documents: only the column whose `parentId === doc.folderId` reloads — the document's
   *   row label is the only thing that changed, and its position in the column may have changed
   *   too if the active sort is name-asc/desc.
   * - For folders: the column whose `parentId === folder.parentId` reloads (so the row label
   *   updates), AND every column whose `parentId === folder.id` updates its `parentLabel` so
   *   the column header (and the breadcrumb crumb) reflects the new name without a refetch.
   */
  protected onRenamed(event: RenameNotice): void {
    this.columns.update((cols) =>
      cols.map((c) => {
        let next = c;
        if (c.parentId === event.parentId) {
          next = { ...next, reloadKey: next.reloadKey + 1 };
        }
        if (event.kind === 'folder' && c.parentId === event.id) {
          next = { ...next, parentLabel: event.name };
        }
        return next;
      }),
    );
  }

  /**
   * After tags change in the detail pane, refresh the column where the affected row lives so its
   * chip strip re-renders with the new set. Same column-pick logic as the rename handler:
   * `parentId` is the row's folder for documents and the row's parent for folders.
   *
   * Also refresh the smart columns (Recent / Starred / Trash): the same row can be visible there
   * with its chip strip, and those columns have `parentId === null`, so `refreshColumnContaining`
   * never matches them. Without this the new tag stayed hidden in the smart list until a manual
   * refresh or navigating away and back (TF-1980). Mirrors the lifecycle handlers below.
   */
  protected onTagsUpdated(event: { kind: 'folder' | 'document'; id: string; parentId: string | null }): void {
    this.refreshColumnContaining(event.parentId);
    this.refreshSmartColumns();
  }

  /**
   * After soft-delete or restore, refresh the column the affected row lives in so it drops out of (or
   * returns to) the live listing. Detail-pane selection is intentionally kept on the same row so
   * the user can immediately Restore (after soft-delete) or keep working on the row (after restore)
   * without re-clicking. Smart columns (Recent / Starred / Trash) also reload because the
   * lifecycle event invalidates their listing — e.g. restoring a soft-deleted row removes it from
   * the Trash smart column and an active doc that was just soft-deleted needs to drop from
   * Recent and Starred.
   *
   * Also drops the affected id from {@link starredIds} so the header star icon flips off
   * immediately. The backend's soft-delete path drops the matching star row, and restore brings
   * the row back un-starred, so the local set's invariant is "stars are bound to live state".
   * Mirrors the server-side rule introduced with slice C.
   *
   * If the active selection is a folder whose id matches, refresh its child counts too — a
   * cascade-soft-delete/restore changes the count of direct children visible in the subtitle.
   */
  protected onLifecycleChanged(
    event: { kind: 'folder' | 'document'; id: string; parentId: string | null },
  ): void {
    if (this.starredIds().has(event.id)) {
      this.starredIds.update((ids) => {
        const next = new Set(ids);
        next.delete(event.id);
        return next;
      });
    }

    if (event.kind === 'folder') {
      // Folder hard-delete: the column showing the deleted folder's children (if open to the
      // right) is now stale and must be removed. The previous column's row for the deleted
      // folder is also gone, so we bump its reloadKey and clear its highlight. Selection
      // jumps to the parent so the user lands somewhere meaningful — leaving the focus on
      // the deleted folder showed a blank/error column and felt like the action half-worked.
      this.columns.update((cols) => {
        const deadColumnIdx = cols.findIndex((c) => c.parentId === event.id);
        const truncated = deadColumnIdx >= 0 ? cols.slice(0, deadColumnIdx) : cols;
        return truncated.map((c) => ({
          ...c,
          selectedChildId: c.selectedChildId === event.id ? null : c.selectedChildId,
          reloadKey: c.parentId === event.parentId ? c.reloadKey + 1 : c.reloadKey,
        }));
      });
      this.refreshSmartColumns();
      this.selection.set(event.parentId ? { kind: 'folder', id: event.parentId } : null);
      this.refreshFolderChildCounts(event.parentId);
      // Pre-record scope and update the URL atomically — same pattern as breadcrumb collapse.
      this.lastEffectInitialFolder = event.parentId;
      this.lastEffectInitialView = null;
      this.currentFolderIdChange.emit(event.parentId);
      return;
    }

    this.refreshColumnContaining(event.parentId);
    this.refreshSmartColumns();
    const sel = this.selection();
    if (sel?.kind === 'document' && sel.id === event.id) {
      // The selected document was just soft-deleted, so it's no longer in the live folder. Don't
      // keep it selected — that left the detail pane showing a preview of a file that's gone from
      // the folder. Fall back to the parent folder (folder preview) and drop `?doc=` so a reload
      // doesn't reopen it. Restore lives in the Deleted view.
      this.selection.set(event.parentId ? { kind: 'folder', id: event.parentId } : null);
      this.refreshFolderChildCounts(event.parentId);
      this.currentDocumentIdChange.emit(null);
    } else if (sel?.kind === 'folder' && sel.id === event.parentId) {
      // A document inside the currently-viewed folder was soft-deleted elsewhere — refresh the
      // subtitle counts so they reflect the smaller listing.
      this.refreshFolderChildCounts(event.parentId);
    }
  }

  /**
   * Hard-delete a soft-deleted document. The row is gone from every listing — the smart Trash
   * column refreshes, any column whose parent matches reloads, and the detail-pane selection
   * is cleared (its target no longer exists). `?doc=` is dropped from the URL so a reload
   * doesn't try to re-open a 404.
   */
  protected onHardDeleted(
    event: { kind: 'folder' | 'document'; id: string; parentId: string | null },
  ): void {
    this.refreshColumnContaining(event.parentId);
    this.refreshSmartColumns();
    if (this.starredIds().has(event.id)) {
      this.starredIds.update((ids) => {
        const next = new Set(ids);
        next.delete(event.id);
        return next;
      });
    }
    if (this.selection()?.id === event.id) {
      this.selection.set(null);
      this.folderChildCounts.set(null);
      this.currentDocumentIdChange.emit(null);
    }
  }

  /**
   * Document restore lands the row in a (possibly newly-created) live folder. After the success
   * the user expects the UI to follow the doc to its new location: open the column trail down to
   * the destination folder and select the restored document. We use [restoreDocumentSelection]
   * which already does the fetch + trail-walk + selection in one call.
   *
   * Invalidates the smart columns first so the Trash drops the row.
   */
  protected onDocumentRestored(
    event: { kind: 'folder' | 'document'; id: string; parentId: string | null },
  ): void {
    this.refreshSmartColumns();
    if (event.kind !== 'document') return;
    // Reset the once-per-id guard so the trail walk re-runs even if we navigated to this
    // document earlier on the same trail.
    this.restoredDocumentId = null;
    void this.restoreDocumentSelection(event.id);
  }

  private refreshSmartColumns(): void {
    this.columns.update((cols) =>
      cols.map((c) => (c.smart ? { ...c, reloadKey: c.reloadKey + 1 } : c)),
    );
  }

  /** Bumps the reloadKey on the column whose `parentId` matches, triggering a silent re-fetch. */
  private refreshColumnContaining(parentId: string | null): void {
    this.columns.update((cols) =>
      cols.map((c) => (c.parentId === parentId ? { ...c, reloadKey: c.reloadKey + 1 } : c)),
    );
  }

  /**
   * First-arrow bootstrap. When the user lands on the documents page (e.g. via a sidebar click)
   * focus stays on the sidebar link, so the first arrow press would otherwise go nowhere. We
   * watch document keydowns: if Up / Down / Right is pressed while focus is *outside* the column
   * area (and not in a text input or another combobox / listbox), move focus into the rightmost
   * column's selected row. Because the focus call is in direct response to a keyboard event,
   * the browser treats it as a keyboard-driven focus and `:focus-visible` matches naturally —
   * the focus ring only appears the moment the user actually starts navigating, not on page load.
   */
  @HostListener('document:keydown', ['$event'])
  protected onDocumentKeydown(event: Event): void {
    if (!(event instanceof KeyboardEvent)) return;
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'ArrowRight') return;
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return;
    // Already inside a row — the column's own keydown handler takes over.
    if (active.matches('[role="option"]')) return;
    // Don't steal arrow keys from text inputs, textareas, comboboxes, or other listboxes
    // (sort dropdown, search fields, etc.).
    if (active.matches('input, textarea, select, [contenteditable="true"]')) return;
    if (active.closest('[role="combobox"], [role="listbox"], [role="menuitem"]')) return;
    // Only bootstrap when the documents browser is actually mounted.
    const lastIdx = this.columns().length - 1;
    if (lastIdx < 0) return;
    event.preventDefault();
    this.focusColumnAt(lastIdx, 'selected');
  }

  /**
   * Cross-column keyboard nav. A column emits this when arrow-key navigation walks past its edge.
   * - `'previous'`: focus the selected (or first) row of the column to the left.
   * - `'next'`: focus the first row of the column to the right. The next column may have just
   *   been opened by a Right-arrow activation. The new `<tf-folder-column>` shows up after the
   *   next render tick, but its rows arrive *later* once the async load resolves — so we use a
   *   MutationObserver to focus the first row as soon as one is inserted, with a 1.5s timeout
   *   as a safety net (no rows means the folder is empty / errored, focus stays put).
   *
   * We resolve columns by DOM order rather than `viewChildren`, because the children mix two
   * component types (`tf-folder-column` and `tf-smart-list-column`) and a single ordered list is
   * easier to reason about than two parallel queries that have to be merged by column index.
   */
  protected onColumnFocusOutOfBounds(columnIdx: number, direction: 'previous' | 'next'): void {
    if (direction === 'previous') {
      this.focusColumnAt(columnIdx - 1, 'selected');
      return;
    }
    afterNextRender(
      () => this.focusColumnAt(columnIdx + 1, 'first'),
      { injector: this.injector },
    );
  }

  /**
   * Focus the first / selected row in a column. If the column hasn't loaded its rows yet, watch
   * for them via MutationObserver and focus the first one to appear. Caps the wait at 1500ms so
   * an empty / errored column doesn't leave the observer dangling.
   */
  private focusColumnAt(columnIdx: number, position: 'first' | 'selected'): void {
    if (columnIdx < 0) return;
    const columnElements = this.hostRef.nativeElement.querySelectorAll<HTMLElement>(
      'tf-folder-column, tf-smart-list-column',
    );
    const column = columnElements[columnIdx];
    if (!column) return;

    const focusInColumn = (): boolean => {
      if (position === 'selected') {
        const selected = column.querySelector<HTMLElement>('[role="option"][aria-selected="true"]');
        if (selected) {
          selected.focus();
          return true;
        }
      }
      const first = column.querySelector<HTMLElement>('[role="option"]');
      if (first) {
        first.focus();
        return true;
      }
      return false;
    };

    if (focusInColumn()) return;

    // Rows aren't in the DOM yet (column just opened, async load pending). Watch for the first
    // row insertion. The observer self-disconnects on success or after the timeout.
    const observer = new MutationObserver(() => {
      if (focusInColumn()) {
        observer.disconnect();
        clearTimeout(timeoutId);
      }
    });
    observer.observe(column, { childList: true, subtree: true });
    const timeoutId = setTimeout(() => observer.disconnect(), 1500);
  }

  /**
   * Bump reloadKey on the source and target columns after a successful drag-and-drop move so
   * both refetch their listings. The column where the row was removed updates first (so the
   * row disappears); the column where it landed picks it up if it's currently visible.
   */
  protected onMoveCompleted(event: { sourceParentId: string | null; targetParentId: string | null }): void {
    this.columns.update((cols) =>
      cols.map((c) => {
        if (c.parentId === event.sourceParentId || c.parentId === event.targetParentId) {
          return { ...c, reloadKey: c.reloadKey + 1 };
        }
        return c;
      }),
    );
  }

  /**
   * Re-fetches direct children of `folderId` so the detail pane's "X documents · Y subfolders"
   * subtitle stays in sync with what the user just clicked. Cheap to call — the API responses
   * are usually already cached by the column that just rendered them. Both totals come from
   * `page.totalElements` so the count is correct even when only the first page was returned.
   */
  private async refreshFolderChildCounts(folderId: string | null): Promise<void> {
    if (!folderId) {
      this.folderChildCounts.set(null);
      return;
    }
    try {
      const [folderList, documentList] = await Promise.all([
        this.foldersService.listChildren(folderId, { size: 1 }),
        this.documentsService.listInFolder(folderId, { size: 1 }),
      ]);
      const folders = folderList.page?.totalElements ?? folderList.items?.length ?? 0;
      const documents = documentList.page?.totalElements ?? documentList.items?.length ?? 0;
      this.folderChildCounts.set({ folders, documents });
    } catch {
      this.folderChildCounts.set(null);
    }
  }

  /**
   * Restores the column stack so that `folderId` is the rightmost open column. When
   * `selectedDocumentId` is provided, marks that document as selected on the deepest column and
   * surfaces it in the detail pane instead of the folder; this is what powers the
   * `?doc=<id>`-on-reload deep link.
   */
  private async openTrail(
    folderId: string,
    rootFolderId: string | null,
    selectedDocumentId: string | null = null,
  ): Promise<void> {
    const trail: FolderResponse[] = [];
    let nextId: string | null = folderId;
    while (nextId && nextId !== rootFolderId) {
      try {
        const folder: FolderResponse = await this.foldersService.getById(nextId);
        trail.unshift(folder);
        nextId = folder.parentId ?? null;
      } catch {
        this.columns.set([
          { parentId: rootFolderId, parentLabel: null, selectedChildId: null, selectedDocumentId: null, reloadKey: 0 },
        ]);
        if (rootFolderId && !this.rootLabel()) this.resolveRootLabel(rootFolderId);
        return;
      }
    }

    if (trail.length === 0) {
      this.columns.set([
        { parentId: rootFolderId, parentLabel: null, selectedChildId: null, selectedDocumentId: selectedDocumentId, reloadKey: 0 },
      ]);
      // No descendant trail to render — when no document is being restored, leave selection
      // empty rather than auto-selecting `rootFolderId`. Scoped roots are managed folders that
      // the detail pane shows as empty regardless; clearing here keeps the UX consistent with
      // the init path and avoids an extra fetch.
      this.selection.set(
        selectedDocumentId ? { kind: 'document', id: selectedDocumentId } : null,
      );
      if (rootFolderId && !this.rootLabel()) this.resolveRootLabel(rootFolderId);
      return;
    }

    const firstTrailFolder = trail[0];
    const cols: ColumnState[] = [{
      parentId: rootFolderId,
      parentLabel: null,
      selectedChildId: firstTrailFolder?.id ?? null,
      selectedDocumentId: null,
      reloadKey: 0,
    }];
    for (let i = 0; i < trail.length; i++) {
      const folder = trail[i];
      if (!folder) continue;
      const next = trail[i + 1];
      const isDeepest = i === trail.length - 1;
      cols.push({
        parentId: folder.id ?? null,
        parentLabel: folder.displayName ?? folder.name ?? null,
        parentKind: folder.kind ?? null,
        selectedChildId: next?.id ?? null,
        selectedDocumentId: isDeepest ? selectedDocumentId : null,
        reloadKey: 0,
      });
    }
    this.columns.set(cols);
    const deepest = trail.at(-1);
    if (selectedDocumentId) {
      this.selection.set({ kind: 'document', id: selectedDocumentId });
      this.folderChildCounts.set(null);
    } else if (deepest?.id) {
      this.selection.set({ kind: 'folder', id: deepest.id });
      this.refreshFolderChildCounts(deepest.id);
    }
    if (rootFolderId && !this.rootLabel()) this.resolveRootLabel(rootFolderId);
  }

  // ─── Public read-only accessors (consumed by AI page-context factories) ────────────────

  /**
   * Snapshot of the user's current selection (folder or document, or `null` when nothing's
   * selected). Read by the host page's AI assistant page-context factory so the assistant
   * knows what the user is currently focused on. Exposed read-only — the browser owns the
   * write side; external consumers react via signal effects.
   */
  readonly currentSelection: Signal<DetailSelection | null> = this.selection.asReadonly();

  /**
   * Crumb labels in column order, root-first, deepest-last. Used as the human-readable
   * `entity.label` in the AI page context (e.g. "Documents › Cases › 123") so the assistant
   * can refer to "the current folder" without needing to fetch the folder tree.
   */
  readonly breadcrumbLabels: Signal<readonly string[]> = computed(() =>
    this.breadcrumbs().map((c) => c.label),
  );

  /**
   * Active smart-view kind on the rightmost column when one's selected (recent / starred /
   * deleted). `null` when the user is browsing folders normally. Surfaced as a filter on the
   * page context so the assistant knows which list it's looking at.
   */
  readonly activeSmartView: Signal<SmartListKind | null> = computed(() => {
    const cols = this.columns();
    return cols.at(-1)?.smart ?? null;
  });
}
